/* eslint-disable react/jsx-curly-brace-presence */
/* eslint-disable react/jsx-indent */
import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { fetchEnrolledCourses } from '@actions/courses';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import { emptyObject } from '@constants/empty';
import useExperiments from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getJWTToken } from '@api/selectors/auth';
import { abProxyJWT, abVariations } from '@utils/abHelper';
import useFetch from '@hooks/useFetch';
import EmptyState from '@cont/EnrolledCourse/EmptyState';
import { mediaQuery, Spacings } from '@styles/index';
import FilterBlock from './FilterBlock';
import CourseTypeBlock from './CourseTypeBlock';
import TopBlock from './TopBlock';
import BatchTypeBlock from './BatchTypeBlock';
import CourseBlock from './CourseBlock';

const FiltersWrapper = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoTablet} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const isEnrollmentEmpty = (batchData, courseData, batchDataLoading) =>
  !courseData?.isFetching &&
  courseData?.results?.length === 0 &&
  !batchDataLoading &&
  batchData?.results?.length === 0;

const compare = (a, b) => {
  const label1 = a.label.toUpperCase();
  const label2 = b.label.toUpperCase();

  let comparison = 0;
  if (label1 > label2) {
    comparison = 1;
  } else if (label1 < label2) {
    comparison = -1;
  }
  return comparison;
};

const createFilterItems = (obj, key, sort) => {
  const sourceArr = obj[key];
  const filterItemsArr =
    sourceArr &&
    sourceArr.length > 0 &&
    sourceArr.map((el = emptyObject) => {
      return {
        label: el.value,
        value: el.id
      };
    });

  if (filterItemsArr && sort) {
    filterItemsArr.sort(compare);
  }
  return filterItemsArr || [];
};

const EnrolledCourse = ({ seeAllType }) => {
  const router = useRouter();
  const dispatch = useDispatchToStore();
  const { goalUID, topicGroupUID } = router.query;
  const isCoursesPage = router.pathname.includes('courses');
  const goalName = 'IIT JEE';
  const topBlockLabel = isCoursesPage ? 'Enrolled courses' : 'My enrollments';
  const {
    data: batchData = {},
    isLoading,
    mutate: refetchBatchData
  } = useFetch(
    `v1/uplus/subscription/enrolled_batches/?goal_uid=${goalUID}&limit=4&offset=0`
  );

  const goalInfo = useSelector(({ topologies }) => topologies.data[goalUID]);
  const enrolledCoursesFilters = useSelector(
    ({ courses }) => courses.enrolledCoursesFilters
  );
  const enrolledCourses = useSelectorToStore(
    ({ paginator }) => paginator.enrolledCourses
  );

  const experimentVariation = useExperiments(
    getExperimentVariation('PB_enrolment_restriction')
  );

  // TODO: Remove true condition
  const isPrimaryBatchEnabled =
    (experimentVariation && experimentVariation === abVariations.TEST_1) ||
    true;

  // course state
  const [coursesType, setCoursesType] = useState('');
  const courseTypeItems = createFilterItems(enrolledCoursesFilters, 'state');

  // language
  const [language, setLanguage] = useState('');
  const languageItems = createFilterItems(enrolledCoursesFilters, 'language');

  // educator
  const [educator, setEducator] = useState('');
  const educatorItems = createFilterItems(
    enrolledCoursesFilters,
    'educator',
    true
  );

  // topic
  const [topic, setTopic] = useState(topicGroupUID || '');
  const topicItems = createFilterItems(enrolledCoursesFilters, 'topic');

  const filterObj = useMemo(
    () => ({
      state: coursesType || 'all',
      language: language || 'all',
      educator: educator || 'all',
      topic: topic || 'all'
    }),
    [coursesType, language, educator, topic]
  );

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const meJWT = useMyInfo(getJWTToken);

  useEffect(() => {
    dispatch(fetchEnrolledCourses(goalUID, filterObj, false));
  }, [filterObj, dispatch, goalUID]);

  useEffect(() => {
    fetchABExperimentData(
      'PB_enrolment_restriction',
      isLoggedIn ? meJWT : abProxyJWT
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const enrolledCourseKey = useMemo(() => {
    return Object.keys(filterObj || {}).reduce(
      (acc, key) => {
        let storeKey = acc;
        storeKey += `-${filterObj[key] || 'all'}`;

        return storeKey;
      },
      [`${goalUID}-enrolled`]
    );
  }, [filterObj, goalUID]);

  const data = enrolledCourses?.[enrolledCourseKey];

  const breadCrumbsTags = [
    { title: goalName, href: 'ww.r.r' },
    { title: 'Plus', href: 'w.r.r' }
  ];

  if (isLoading) return null;

  const renderBlocks = () => {
    if (isPrimaryBatchEnabled && !isCoursesPage)
      return (
        <>
          <BatchTypeBlock
            goalUID={goalUID}
            isLoading={isLoading}
            data={batchData}
            refetchBatchData={refetchBatchData}
          />
          <CourseBlock courseData={data} goalUID={goalUID} />
        </>
      );

    return (
      <>
        <FiltersWrapper>
          {topicItems.length > 1 && (
            <FilterBlock
              filterItems={topicItems}
              filterItemOnClick={setTopic}
              filterSelected={topic}
              variant="topic"
            />
          )}
          {courseTypeItems.length > 1 && (
            <FilterBlock
              filterItems={courseTypeItems}
              filterItemOnClick={setCoursesType}
              filterSelected={coursesType}
              variant="course"
            />
          )}
          {educatorItems.length > 1 && (
            <FilterBlock
              filterItems={educatorItems}
              filterItemOnClick={setEducator}
              filterSelected={educator}
              variant="educator"
            />
          )}
          {languageItems.length > 1 && (
            <FilterBlock
              filterItems={languageItems}
              filterItemOnClick={setLanguage}
              filterSelected={language}
              variant="language"
            />
          )}
        </FiltersWrapper>
        <CourseTypeBlock
          courseData={data}
          goalUID={goalUID}
          filterObj={filterObj}
        />
      </>
    );
  };

  return (
    <>
      <TopBlock
        breadCrumbsTags={breadCrumbsTags}
        label={topBlockLabel}
        seeAllType={seeAllType}
        goal={goalInfo}
      />
      {renderBlocks()}
      {isEnrollmentEmpty(batchData, data, isLoading) && (
        <EmptyState goalRelativeLink={goalInfo?.relativeLink} type="courses" />
      )}
    </>
  );
};

export default EnrolledCourse;

EnrolledCourse.propTypes = {
  seeAllType: PropTypes.string.isRequired
};
